import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, CornerDownLeft, Sparkles } from 'lucide-react';
import { useWasmMenuSearch } from './useWasmMenuSearch';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { executeSearch, ready } = useWasmMenuSearch();
  const navigate = useNavigate();

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Auto-focus input on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Give a tiny timeout for modal animation to settle before focusing
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const results = executeSearch(query);

  // Keyboard navigation & controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (results.length > 0 ? (prev + 1) % results.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (results.length > 0 ? (prev - 1 + results.length) % results.length : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) {
          navigate(results[selectedIndex].route);
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose, navigate]);

  // Keep selected item visible in scroll view
  useEffect(() => {
    if (resultsRef.current) {
      const activeEl = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 sm:px-6">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Dialog Container */}
      <div className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl transition-all duration-300 flex flex-col max-h-[80vh]">
        {/* Search Bar Header */}
        <div className="relative flex items-center border-b border-slate-100 dark:border-slate-800 px-4 py-4">
          <Search className="w-5 h-5 text-slate-400 mr-3 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            className="w-full text-slate-900 dark:text-slate-100 placeholder-slate-400 bg-transparent outline-none text-base border-none focus:ring-0"
            placeholder="Search menus, features, documentations..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
          />
          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-[11px] font-medium text-slate-400 select-none">
            <span className="text-xs">ESC</span>
          </div>
        </div>

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto p-2 min-h-[100px] max-h-[400px]" ref={resultsRef}>
          {!ready && (
            <div className="py-12 text-center text-sm text-slate-500">
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-500 border-t-transparent mr-2"></div>
              Loading high-performance search index...
            </div>
          )}

          {ready && query.trim() === '' && (
            <div className="py-8 text-center">
              <Sparkles className="w-8 h-8 text-(--primary) mx-auto mb-2 opacity-60" />
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Quick Navigation</p>
              <p className="text-xs text-slate-400 mt-1">Start typing to search tenants, users, roles, activities, and guides.</p>
            </div>
          )}

          {ready && query.trim() !== '' && results.length === 0 && (
            <div className="py-12 text-center text-sm text-slate-400">
              No results found for "<span className="font-semibold text-slate-600 dark:text-slate-300">{query}</span>"
            </div>
          )}

          {ready && results.map((result, index) => {
            const isSelected = index === selectedIndex;
            return (
              <button
                key={result.route}
                onClick={() => {
                  navigate(result.route);
                  onClose();
                }}
                className={`w-full text-left flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-150 mb-1 group border border-transparent ${isSelected
                  ? 'bg-(--primary) text-white shadow-lg shadow-(--primary)/15 border-(--primary)'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-300'
                  }`}
              >
                <div className="flex flex-col min-w-0 pr-4">
                  <span className={`font-semibold text-sm leading-tight ${isSelected ? 'text-white' : 'text-slate-900 dark:text-slate-100'
                    }`}>
                    {result.title}
                  </span>
                  <span className={`text-xs mt-1 truncate ${isSelected ? 'text-white/80' : 'text-slate-400'
                    }`}>
                    {result.route}
                  </span>
                </div>

                {/* Arrow indicator on selection */}
                <div className="flex items-center shrink-0">
                  {isSelected ? (
                    <div className="flex items-center gap-1 text-[10px] bg-white/20 px-2 py-0.5 rounded font-mono text-white/90">
                      <span>Navigate</span>
                      <CornerDownLeft className="w-3 h-3" />
                    </div>
                  ) : (
                    <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-700 group-hover:translate-x-0.5 transition-transform" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer shortcuts */}
        <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 px-4 py-3 bg-slate-50/50 dark:bg-slate-900/20 text-[11px] text-slate-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="font-semibold font-mono border border-slate-200 dark:border-slate-800 px-1 rounded bg-white dark:bg-slate-800">↑↓</span> Move</span>
            <span className="flex items-center gap-1"><span className="font-semibold font-mono border border-slate-200 dark:border-slate-800 px-1 rounded bg-white dark:bg-slate-800">Enter</span> Select</span>
          </div>
          <div className="flex items-center gap-1 select-none">
            {/* <span>Powered by</span>
            <span className="font-bold text-(--primary) flex items-center gap-0.5">
              Rust WASM <Command className="w-2.5 h-2.5" />
            </span> */}
          </div>
        </div>
      </div>
    </div>
  );
}
